<template>
  <div>
    <b-card>
      <h2 v-if="!readonly" v-translate>Search and Edit</h2>
      <b-row>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-area-from" v-translate>Area From</label>
            <b-form-select id="quote-area-from" v-model="filter.quoteAreaFrom" :options="areasSelect">
              <template #first>
                <option :value="null" v-translate>Select area</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-area-to" v-translate>Area To</label>
            <b-form-select id="quote-area-to" v-model="filter.quoteAreaTo" :options="areasSelect">
              <template #first>
                <option :value="null" v-translate>Select area</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-name" v-translate>Postal Code</label>
            <input id="quote-name" v-model.lazy="filter.quotePostalCode" type="text" class="mb-2 col-12 form-control">
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-shipping-type" v-translate>Shipping Type</label>
            <b-form-select id="quote-shipping-type" v-model="filter.quoteShippingType" :options="this.shippingTypes.map(x => {return {value: x.id, text: x.name}})"></b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Size</label>
            <b-form-select id="quote-size" v-model="filter.quoteShippingSizeType" :options="this.sizeTypes.map(x => {return {value: x.id, text: x.name}})"></b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4" v-if="!readonly">
          <div class="form-group">
            <label for="quote-size" v-translate>Imported Quotes CSV</label>
            <b-form-file
              v-model="fileImported"
              :placeholder="$gettext('Choose an Imported CSV or drop it here...')"
              :drop-placeholder="$gettext('Drop file here...')"
              @input="handleFileImportedSelect"
              ref="importedQuotes"
              id="importedQuotes"
              accept=".csv"
            ></b-form-file>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Client</label>
            <company-autocomplete ref="companyAc" @selected="onSearchCompanySelected" v-if="filter.quoteCompanies.length === 0" />
            <div v-for="(item, index) in filter.quoteCompanies" :key="index" v-else>
              {{ item.name }} <b-badge variant="danger" href="#" @click.prevent="removeCompanyFromFilters(index)">×</b-badge>
            </div>&nbsp;
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Provider</label>
            <company-autocomplete ref="providerAc" @selected="onSearchProviderSelected" v-if="filter.quoteProviders.length === 0" />
            <div v-for="(item, index) in filter.quoteProviders" :key="index" v-else>
              {{ item.name }} <b-badge variant="danger" href="#" @click.prevent="removeProviderFromFilters(index)">×</b-badge>
            </div>&nbsp;
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <label for="quote-size">&nbsp;</label>
          <b-form-row>
            <b-col>
              <b-button size="sm" block variant="success" @click="searchQuotes" class="mb-2" :disabled="working">
                <b-spinner small v-if="working" />
                <translate>Search</translate>
              </b-button>
            </b-col>
            <b-col>
              <b-button size="sm" block variant="danger" @click="resetQuoteSearch" class="mb-2" :disabled="working">
                <translate>Reset Search</translate>
              </b-button>
            </b-col>
          </b-form-row>
        </b-col>
      </b-row>
      <div v-if="searchedQuotes.length && !readonly" class="my-3">
        <b-form-checkbox inline v-model="selectAllTrigger"><translate>Select/Unselect All</translate></b-form-checkbox>
        <b-button variant="primary" @click="backupSelected" :disabled="selected.length === 0 || deleting" v-translate>
          Backup Selected
        </b-button>
        <b-button variant="warning" v-b-modal.modalEditPrices :disabled="selected.length === 0 || !backuped || deleting" v-translate>
          EDIT Selected
        </b-button>
        <b-button variant="danger" @click="deleteSelected" :disabled="selected.length === 0 || !backuped || deleting" v-translate>
          DEACTIVATE Selected
        </b-button>
      </div>
      <div v-if="searchedQuotes.length && readonly" class="my-3">
        <b-form-checkbox inline v-model="selectAllTrigger"><translate>Select/Unselect All</translate></b-form-checkbox>
        <b-button variant="warning" v-b-modal.modalEditPrices v-translate>
          VIEW Selected
        </b-button>
      </div>
      <b-modal id="modalEditPrices" size="xl" ref="modalEditPrices" hide-footer @hidden="resetEditPrices">
        <div slot="modal-title" v-translate>Bulk Edit Prices</div>
        <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
        <b-row v-if="updating">
          <b-col>
            <b-progress :max="totalToUpdate.length" height="2rem" class="mb-3">
              <b-progress-bar :value="updatedCount">
                <span v-translate="{count: updatedCount, total: totalToUpdate.length}">Progress: <strong>%{count} / %{total}</strong></span>
              </b-progress-bar>
            </b-progress>
          </b-col>
        </b-row>
        <b-row v-if="!readonly">
          <b-col>
            <h4 v-translate>Activate/Deactivate</h4>
            <b-row>
              <b-col sm="4">
                <b-row>
                  <b-col sm="5">
                    <label for="search-for" v-translate>Set All Selected As</label>
                  </b-col>
                  <b-col sm="7">
                    <b-form-select id="search-for" v-model="active" :options="[{text: $gettext('Active'), value: 1}, {text: $gettext('Not Active'), value: 0}]"></b-form-select>
                  </b-col>
                </b-row>
              </b-col>
              <!-- <b-col sm="4">
                <b-row>
                  <b-col sm="5">
                    <label for="replace-with" v-translate>Replace with:</label>
                  </b-col>
                  <b-col sm="7">
                    <b-form-input id="replace-with" v-model="replaceWith"></b-form-input>
                  </b-col>
                </b-row>
              </b-col> -->
              <b-col sm="4">
                <b-button @click="bulkReplaceActive" :disabled="updating" v-translate>Bulk Set Active Status</b-button>
              </b-col>
            </b-row>
            <hr>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="3">
            <h4 v-translate>Prices</h4>
            <b-row class="mb-3" v-if="!readonly">
              <b-col sm="5">
                <label for="price-by-percentage" v-translate>Change All By Percentage</label>
              </b-col>
              <b-col sm="7">
                <b-input-group append="%">
                  <b-form-input id="price-by-percentage" v-model="priceByPercentage" :disabled="pricesIsNan"></b-form-input>
                </b-input-group>
                <b-form-checkbox :disabled="pricesIsNan" v-model="pricesAsIntegers"><translate>Force Integers</translate></b-form-checkbox>
              </b-col>
            </b-row>
            <b-row class="mb-1" v-for="(price, i) in distinctPrices" :key="'price' + i">
              <b-col sm="5">
                <label :for="`price-${i}`">{{ price }}</label>
              </b-col>
              <b-col sm="7" v-if="!readonly">
                <b-form-input :id="`price-${i}`" v-model="newPrices[price]"></b-form-input>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-button @click="bulkUpdatePrices" :disabled="updating" v-if="!readonly" v-translate>Update</b-button>
      </b-modal>
      <bulk-delete-progress v-bind="bulkDeleteProps" v-on="bulkDeleteListeners" />
      <p v-if="total" v-translate="{total: this.total}">Total: <strong>%{total}</strong></p>
      <b-table id="quoteProvidersTable" ref="quoteProvidersTable" @row-selected="rowSelected" :busy.sync="working" selectable stacked="md" foot-clone :items="searchedQuotes" :fields="quotesFields" v-if="searchedQuotes.length > 0 && !deleting">
        <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong v-translate>Loading...</strong>
        </div>
        <template v-slot:cell(reference)="data">
          <a :href="'/edit/outsourcing-quotes/' + data.item.id" target="_blank" v-if="!readonly">{{data.item.reference}}</a>
          <span v-else>{{data.item.reference}}</span>
        </template>
        <template v-slot:cell(size_id)="data">
          {{ data.item.size_id ? sizeTypes.findIndex(x => x.id === data.item.size_id) !== -1 ? sizeTypes.find(x => x.id === data.item.size_id).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_from)="data">
          {{ data.item.area_id_from ? areas.findIndex(x => x.id === data.item.area_id_from) !== -1 ? areas.find(x => x.id === data.item.area_id_from).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_to)="data">
          {{ data.item.area_id_to ? areas.findIndex(x => x.id === data.item.area_id_to) !== -1 ? areas.find(x => x.id === data.item.area_id_to).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(platform_id)="data">
          {{ data.item.platform_id ? platforms.find(x => x.id === data.item.platform_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(shipping_type_id)="data">
          {{ data.item.shipping_type_id ? shippingTypes.find(x => x.id === data.item.shipping_type_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(company_id_customer)="row">
          <strong class="badge" v-if="row.item.company" :style="`background-color: ${stringToColour(' ' + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id)};${textContrastColor(stringToColour(' ' + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id + row.item.company.id))}`">{{row.item.company.name || row.item.company.id}}</strong>
        </template>
        <template v-slot:cell(company_id_provider)="row">
          <strong class="badge" :style="`background-color: ${stringToColour(' ' + row.item.provider.id + row.item.provider.id + row.item.provider.id + row.item.provider.id + row.item.provider.id)};${textContrastColor(stringToColour(' ' + row.item.provider.id + row.item.provider.id + row.item.provider.id + row.item.provider.id + row.item.provider.id))}`">{{row.item.provider.name || row.item.provider.id}}</strong>
        </template>
        <template v-slot:cell(applies_automatically)="row">
          <strong class="text-success" v-if="row.value" v-translate>YES</strong>
          <strong class="text-danger" v-else v-translate>NO</strong>
        </template>
        <template v-slot:cell(active)="row">
          <strong class="text-success" v-if="row.value" v-translate>YES</strong>
          <strong class="text-danger" v-else v-translate>NO</strong>
        </template>
      </b-table>
    </b-card>
    <b-card v-if="!readonly">
      <h2 v-translate>Import</h2>
      <b-form-file
        v-model="file"
        :placeholder="$gettext('Choose a file or drop it here...')"
        :drop-placeholder="$gettext('Drop file here...')"
        @input="handleFileSelect"
        ref="csvToImport"
        id="csvToImport"
        accept=".csv"
      ></b-form-file>
      <b-alert variant="success" :show="importing" class="mt-3">
        <span v-translate="{count: count, total: importData.length}">Importing %{count}/%{total}</span>
      </b-alert>
      <b-alert variant="info" :show="importData.length > 0 && errors.length === 0" class="mt-3">
        <h5 v-translate="{total: importData.length}">It seems there are no errors and you are about to bulk import %{total} quotes. Is it OK?</h5>
        <b-button variant="success" @click="bulkImport" :disabled="importing">
          <b-spinner small v-show="importing" />
          <translate>OK! Let's import them!</translate>
        </b-button>
        <b-button variant="danger" @click="file = null; importData = []" :disabled="importing" v-translate>
          NOPE! Cancel it!
        </b-button>
      </b-alert>
      <b-alert variant="danger" :show="errors.length > 0" class="mt-3">
        <h5 v-translate>It seems there are errors, check 'em out:</h5>
        <ul>
          <li v-for="(item, index) in errors" :key="index">
            {{ item.message }}{{item.row ? ' (row: ' + item.row + (item.index ? ' - col: ' + item.index : '') + ')' : ''}}
          </li>
        </ul>
        <p v-translate>Please, review and fix your CSV file and upload it again</p>
      </b-alert>
    </b-card>
  </div>
</template>
<script>
import { getCountry } from '@/services/country'

  import CompanyAutocomplete from '@/components/autocompletes/CompanyAutocomplete'
  import BulkDeleteProgress from '@/components/BulkDeleteProgress'
  import bulkDeletePool from '@/mixins/bulkDeletePool.mixin'
  import Papa from 'papaparse'

  export default {
    name: 'BulkQuote3PList',
    components: {
      CompanyAutocomplete,
      BulkDeleteProgress
    },
    mixins: [bulkDeletePool],
    props: {
      readonly: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        file: null,
        fileImported: null,
        importData: [],
        errors: [],
        quotesFields: [
          {key: 'company_id_customer', label: this.$gettext('Client')},
          {key: 'company_id_provider', label: this.$gettext('Provider')},
          {key: 'area_id_from', label: this.$gettext('From')},
          {key: 'area_id_to', label: this.$gettext('To')},
          {key: 'postal_code_to', label: this.$gettext('Postal Code')},
          {key: 'shipping_type_id', label: this.$gettext('Type')},
          {key: 'size_id', label: this.$gettext('Size')},
          {key: 'price', label: this.$gettext('Price')},
          {key: 'applies_automatically', label: this.$gettext('Applies Automatically')},
          {key: 'distribution_factor', label: this.$gettext('Distribution Factor')},
          {key: 'active', label: this.$gettext('Active')}
        ],
        filters: [],
        filter: {
          ids: [],
          quoteName: '',
          quoteIncludes: null,
          quoteAreaFrom: null,
          quoteAreaTo: null,
          quoteCompanies: [],
          quoteProviders: [],
          quoteShippingType: null,
          quoteShippingSizeType: null
        },
        working: false,
        searchedQuotes: [],
        companiesQuotes: [],
        companyAcOptions: {
          debounce: 500,
          placeholder: this.$gettext('Company Name, Business Name or TIN'),
          inputClass: 'form-control'
        },
        searchCompanyQuery: '',
        minCharacter: 3,
        areas: [],
        platforms: [],
        platformsSelect: [],
        shippingTypes: [],
        shippingTypesSelect: [],
        sizeTypes: [],
        sizeTypesSelect: [],
        selected: [],
        selectAllTrigger: false,
        backuped: false,
        importing: false,
        // `count` belongs to bulkImport only; the delete flow keeps its own state
        // in bulkDeletePool.mixin so both progress bars stay independent
        count: 0,
        // one step per row: a 3P quote has no children to unlink, so there is no
        // reverse-creation chain and no orphans a mid-chain failure could leave
        deletePhaseKeys: ['outsourcingQuotes'],
        deletePhaseLabels: {outsourcingQuotes: this.$gettext('outsourcing-quotes')},
        // outsourcing-quotes has no `reference` field, so deleteRowIdentity below
        // builds the label instead; this only names the CSV's second column
        deleteRowLabelField: 'client_provider',
        deleteErrorCsvIdColumn: 'outsourcing_quote_id',
        deleteErrorsCsvName: 'quote_3p',
        batchUpdateChunkLength: 15,
        total: null,
        updating: false,
        totalToUpdate: 0,
        updatedCount: 0,
        priceByPercentage: null,
        newPrices: {},
        pricesAsIntegers: false,
        searchFor: null,
        replaceWith: null,
        active: 1
      }
    },
    computed: {
      distinctPrices() {
        return [...new Set(this.selected.map(row => row.price))]
      },
      pricesIsNan() {
        return !!this.distinctPrices.filter(x => isNaN(x))[0]
      }
    },
    watch: {
      priceByPercentage(val) {
        if (!isNaN(val)) {
          for (let i = 0; i < this.distinctPrices.length; i++) {
            this.newPrices[this.distinctPrices[i]] = parseFloat(this.distinctPrices[i]) + parseFloat(this.distinctPrices[i]) * val / 100
          }
        } else {
          this.newPrices = {}
        }
      },
      'filter.ids': {
        handler (val) {
          if (!this.resetting) {
            if (val.length) {
              this.filters.quoteIds = [ 'id', 'in', val ]
            } else {
              this.filters.quoteIds = []
            }
          }
        },
        deep: true
      },
      'filter.quotePostalCode': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters.quotePostalCode = [ 'postal_code_to', 'eq', val ]
            } else {
              this.filters.quotePostalCode = []
            }
          }
        },
        deep: true
      },
      'filter.quoteName': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters.quoteName = [ 'reference', 'ct', val ]
            } else {
              this.filters.quoteName = []
            }
          }
        },
        deep: true
      },
      'filter.quoteAreaFrom': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters.quoteAreaFrom = [ 'area_id_from', 'eq', val ]
            } else {
              this.filters.quoteAreaFrom = []
            }
          }
        },
        deep: true
      },
      'filter.quoteAreaTo': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters.quoteAreaTo = [ 'area_id_to', 'eq', val ]
            } else {
              this.filters.quoteAreaTo = []
            }
          }
        },
        deep: true
      },
      'filter.quoteCompanies': {
        handler (val) {
          if (!this.resetting) {
            if (val.length > 0) {
              const companies = val.map(function (item) {
              return item.id
            })
              this.filters.quoteCompanies = [ 'company_id_customer', 'in', companies ]
            } else {
              this.filters.quoteCompanies = []
            }
          }
        },
        deep: true
      },
      'filter.quoteProviders': {
        handler (val) {
          if (!this.resetting) {
            if (val.length > 0) {
              const companies = val.map(function (item) {
              return item.id
            })
              this.filters.quoteProviders = [ 'company_id_provider', 'in', companies ]
            } else {
              this.filters.quoteProviders = []
            }
          }
        },
        deep: true
      },
      'filter.quoteShippingType': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters.quoteShippingType = [ 'shipping_type_id', 'eq', val ]
            } else {
              this.filters.quoteShippingType = []
            }
          }
        },
        deep: true
      },
      'filter.quoteShippingSizeType': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters.quoteShippingSizeType = [ 'size_id', 'eq', val ]
            } else {
              this.filters.quoteShippingSizeType = []
            }
          }
        },
        deep: true
      },
      selectAllTrigger(val) {
        if (val === true) {
          this.selectAll()
        } else {
          this.unselectAll()
        }
      },
      selected(val) {
      }
    },
    created() {
      this.fetchAreas()
      this.$http.get(this.$appConfig.api.baseUrl + 'shipping-types')
        .then(response => {
          this.shippingTypes = response.body.data
          this.shippingTypesSelect = this.shippingTypes.map((item) => {
            return {
              value: item.id,
              text: item.name
            }
          })
          this.shippingTypes.unshift({
            id: 'null',
            name: this.$gettext('No specific type')
          })
          this.shippingTypes.unshift({
            id: '',
            name: this.$gettext('All types')
          })
        })
      this.$http.get(this.$appConfig.api.baseUrl + 'sizes')
        .then(response => {
          this.sizeTypes = response.body.data
          this.sizeTypesSelect = this.sizeTypes.map((item) => {
            return {
              value: item.id,
              text: item.name
            }
          })
          this.sizeTypes.unshift({
            id: 'null',
            name: this.$gettext('No specific size')
          })
          this.sizeTypes.unshift({
            id: '',
            name: this.$gettext('All sizes')
          })
          for (let i = 0; i < this.sizeTypesSelect.length; i++) {
            for (let n = 0; n < this.companyPrices.length; n++) {
              if (this.companyPrices[n].size_type_id === this.sizeTypesSelect[i].value) {
                this.sizeTypesSelect.splice(i, 1)
              }
            }
          }
        }).catch(response => {

        })
    },
    methods: {
      resetEditPrices() {
        this.newPrices = {}
        this.priceByPercentage = null
        this.searchFor = null
        this.replaceWith = null
      },
      async bulkReplaceActive() {
        this.updating = true
        this.totalToUpdate = this.selected
        this.updatedCount = 0
        let toUpdate = []
        for (let i = 0; i < this.selected.length; i++) {

          const payload = {
            active: this.active
          }
          if (Object.keys(payload).length !== 0) {
            if (toUpdate.length === this.batchUpdateChunkLength) {
              await Promise.all(toUpdate)
                .then(r => {
                  toUpdate = []
                  this.updatedCount = this.updatedCount + r.length
                })
            }
            toUpdate.push(this.$http.patch(this.$appConfig.api.baseUrl + 'outsourcing-quotes/' + this.selected[i].id, payload))
          }
        }
        if (toUpdate.length > 0) {
          await Promise.all(toUpdate)
            .then(r => {
              toUpdate = []
              this.updatedCount = this.updatedCount + r.length
            })
        }
        this.updating = false
        this.searchQuotes()
        this.$refs.modalEditPrices.hide()
      },
      async bulkUpdatePrices() {
        this.updating = true
        this.totalToUpdate = this.selected.filter(x => Object.keys(this.newPrices).indexOf(x.price) !== -1)
        this.updatedCount = 0
        let toUpdate = []
        for (let i = 0; i < this.selected.length; i++) {
          const payload = {}
          if (this.newPrices[this.selected[i].price]) {
            payload.price = this.newPrices[this.selected[i].price]
            if (this.pricesAsIntegers && !isNaN(payload.price)) {
              payload.price = parseInt(payload.price)
            }
          }
          if (Object.keys(payload).length !== 0) {
            if (toUpdate.length === this.batchUpdateChunkLength) {
              await Promise.all(toUpdate)
                .then(r => {
                  toUpdate = []
                  this.updatedCount = this.updatedCount + r.length
                })
            }
            toUpdate.push(this.$http.patch(this.$appConfig.api.baseUrl + 'outsourcing-quotes/' + this.selected[i].id, payload))
          }
        }
        if (toUpdate.length > 0) {
          await Promise.all(toUpdate)
            .then(r => {
              toUpdate = []
              this.updatedCount = this.updatedCount + r.length
            })
        }
        this.updating = false
        this.searchQuotes()
        this.$refs.modalEditPrices.hide()
      },
      handleFileImportedSelect(evt) {
        const file = this.fileImported
        if (file) {
          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            skipEmptyLines: true,
            complete: (results) => {
              this.filter.ids = results.data.map(id => { return id.quote_id })
            }
          })
        }
      },
      handleFileSelect(evt) {
        const file = this.file
        if (file) {
          const _this = this

          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            skipEmptyLines: true,
            complete: function (results) {
              _this.errors = []
              _this.importData = []
              if (results.errors.length === 0) {
                if (
                  results.meta.fields.indexOf('area_id_from') !== -1 &&
                  results.meta.fields.indexOf('area_id_to') !== -1 &&
                  results.meta.fields.indexOf('company_id_provider') !== -1 &&
                  results.meta.fields.indexOf('price') !== -1
                ) {
                  _this.importData = results.data
                  _this.validatePriceCommas(results.data)
                  _this.validateCatalogIds(results.data)
                  // console.log((_this.importData[1].companies + '').split(','))
                } else {
                  _this.errors = [
                    {
                      message: _this.$gettext('There are one or more missing required data columns')
                    }
                  ]
                }
              } else {
                _this.errors = results.errors
              }
            }
          })
        }
      },
      validateCatalogIds(rows) {
        const catalogFields = [
          // areas is fetched scoped to the active country, sizes and shipping types are global
          {field: 'area_id_from', catalog: this.areas, countryScoped: true},
          {field: 'area_id_to', catalog: this.areas, countryScoped: true},
          {field: 'size_id', catalog: this.sizeTypes},
          {field: 'shipping_type_id', catalog: this.shippingTypes}
        ]
        // best-effort: catalogs still loading are skipped, the backend remains the authority
        const checks = catalogFields
          .filter(x => { return x.catalog && x.catalog.length > 0 })
          .map(x => {
            return {
              field: x.field,
              countryScoped: x.countryScoped,
              // 'null' and '' are the "All"/"No specific" sentinel entries, not real ids
              ids: new Set(x.catalog.map(item => { return item.id }).filter(id => { return id !== 'null' && id !== '' }))
            }
          })
        for (let i = 0; i < rows.length; i++) {
          for (const check of checks) {
            const value = rows[i][check.field]
            if (value !== null && value !== undefined && value !== '' && !check.ids.has(value)) {
              this.errors.push({
                message: this.$gettextInterpolate(
                  // an id from another country exists, it is just missing from the
                  // country-scoped catalog — saying so tells the operator what to clean
                  check.countryScoped
                    ? this.$gettext("Id %{value} in '%{field}' does not exist in the active country")
                    : this.$gettext("Id %{value} in '%{field}' does not exist"),
                  {value, field: check.field}
                ),
                // + 2: with header: true, data row i is line i + 2 of the CSV file
                row: i + 2
              })
            }
          }
        }
      },
      validatePriceCommas(rows) {
        const priceFields = ['price', 'distribution_factor']
        for (let i = 0; i < rows.length; i++) {
          for (const field of priceFields) {
            const value = rows[i][field]
            // dynamicTyping parses a valid number as a number, so a value left as a
            // string with a comma is a decimal comma that would travel corrupted
            if (typeof value === 'string' && value.includes(',')) {
              this.errors.push({
                message: this.$gettextInterpolate(
                  this.$gettext("Decimal comma detected in '%{field}' — decimals must use a dot"),
                  {field}
                ),
                row: i + 2
              })
            }
          }
        }
      },
      async bulkImport() {
        this.importing = true
        this.count = 0
        this.errors = []
        const created = []
        for (let i = 0; i < this.importData.length; i += this.batchUpdateChunkLength) {
          const chunk = this.importData.slice(i, i + this.batchUpdateChunkLength)
          // Promise.allSettled is unavailable with the project's babel runtime (core-js 2)
          const results = await Promise.all(chunk.map((row, j) => {
            return this.importQuote3PRow(row, i + j + 2)
              .then(value => { return {status: 'fulfilled', value} })
              .catch(reason => { return {status: 'rejected', reason} })
          }))
          for (const result of results) {
            if (result.status === 'fulfilled') {
              created.push(result.value)
              this.count++
            } else {
              this.errors.push({message: result.reason.message, row: result.reason.row})
            }
          }
        }
        // sorted once at the end: chunked rows do not fail in CSV order
        this.errors = this.errors.sort((a, b) => a.row - b.row)
        this.file = null
        this.importData = []
        this.importing = false
        if (created.length > 0) {
          const ids = created.map(x => { return {quote_id: x.quoteId} })
          this.exportToCsv('imported_quote_3p__' + created[created.length - 1].createdAt + '.csv', Papa.unparse(ids))
          // list by the exact created ids — a created_at window misses rows because chunked rows don't finish in chronological order
          const createdIds = created.map(x => { return x.quoteId })
          this.filter.ids = createdIds
          this.filters.quoteIds = ['id', 'in', createdIds]
          await this.searchImportedQuotes(createdIds)
        } else {
          this.searchQuotes()
        }
      },
      async importQuote3PRow(row, rowNumber) {
        const quote = JSON.parse(JSON.stringify(row).replace(/{([a-zA-Z]+):[^}]+}/g, '{$1}'))
        const paramsQuotes = {
          area_id_from: quote.area_id_from,
          area_id_to: quote.area_id_to,
          postal_code_to: quote.postal_code_to,
          company_id_customer: quote.company_id_customer,
          company_id_provider: quote.company_id_provider,
          price: quote.price,
          size_id: quote.size_id,
          shipping_type_id: quote.shipping_type_id,
          applies_automatically: quote.applies_automatically,
          distribution_factor: quote.distribution_factor,
          active: quote.active
        }
        try {
          // a row is a single POST, so there is no partial state to roll back
          const response = await this.$http.post(this.$appConfig.api.baseUrl + 'outsourcing-quotes', paramsQuotes)
          return {quoteId: response.body.id, createdAt: response.body.created_at}
        } catch (originalError) {
          // the global Vue.http interceptor opens a blocking simplert on 422/5xx, so
          // without this the operator would have to dismiss one modal per failed row
          this.$root.$children[0].$refs.simplert.justCloseSimplert()
          const error = new Error(
            (originalError && originalError.body && originalError.body.message) ||
            (originalError && originalError.message) ||
            this.$gettext('Import request failed')
          )
          error.row = rowNumber
          throw error
        }
      },
      async searchQuotes() {
        this.selectAllTrigger = false
        this.total = null
        this.working = true
        const params = {
        page: 0,
        limit: 500,
        sort: [
          {
            key: 'id',
            direction: 'desc'
          }
        ],
        includes: [
          'company',
          'provider',
          'area_from',
          'area_to',
          'size',
          'shipping_type'
        ],
        filter_groups: []
      }
        if (!this.$cookies.get('bypassCountry')) {
          params.filter_groups.push(
            {
              filters: [
                [this.$appConfig.models['outsourcing-quotes'].countrize, 'eq', getCountry()]
              ]
            }
          )
        }
        if (this.filters) {
          params.filter_groups.push(
            {filters: Object.values(this.filters)}
          )
        }
        // filters go as a JSON string param instead of the bracket-expanded
        // querystring, which explodes URL limits with array filters (e.g. ids).
        // Empty filters are dropped first: the bracket encoding omits them, but
        // JSON.stringify would send them explicitly as []
        params.filters = JSON.stringify({
          filter_groups: params.filter_groups
            .map(group => {
              return {filters: group.filters.filter(f => { return Array.isArray(f) && f.length > 0 })}
            })
            .filter(group => { return group.filters.length > 0 })
        })
        delete params.filter_groups
        await this.doSearchQuotes(params)
        this.working = false
      },
      async searchImportedQuotes(createdIds) {
        this.selectAllTrigger = false
        this.total = null
        this.working = true
        this.searchedQuotes = []
        // the created ids are the only filter needed, sent as a JSON string param
        // (the bracket-expanded querystring explodes URL limits) and in chunks,
        // because even as JSON the ids travel in the URL
        const chunkSize = 250
        for (let i = 0; i < createdIds.length; i += chunkSize) {
          const params = {
            page: 0,
            limit: 500,
            sort: [
              {
                key: 'id',
                direction: 'desc'
              }
            ],
            includes: [
              'company',
              'provider',
              'area_from',
              'area_to',
              'size',
              'shipping_type'
            ],
            filters: JSON.stringify({filter_groups: [{filters: [['id', 'in', createdIds.slice(i, i + chunkSize)]]}]})
          }
          await this.doSearchQuotes(params, this.searchedQuotes)
        }
        this.working = false
      },
      async doSearchQuotes(params, prevQuotes = []) {
        let searchedQuotes = []
        return await this.$http.get(this.$appConfig.api.baseUrl + this.$appConfig.models['outsourcing-quotes'].endpoint, {params})
          .then(async response => {
            searchedQuotes = response.body.data

            searchedQuotes = prevQuotes.concat(searchedQuotes)
            if (response.body.data.length !== 0) {
              params.page = params.page + 1
              await this.doSearchQuotes(params, searchedQuotes)
            } else {
              this.total = searchedQuotes.length
              this.searchedQuotes = searchedQuotes
            }
          })
      },
      resetQuoteSearch() {
        this.total = null
        this.resetting = true
        this.searchedQuotes = []
        this.filters = []
        this.fileImported = null
        Object.assign(this.$data.filter, this.$options.data.call(this).filter)
        setTimeout(function () {
          this.resetting = false
        }.bind(this), 500)
      },
      rowSelected(items) {
        this.selected = items
      },
      selectAll() {
        // selectAllRows() selects in one pass; clicking every <tr> meant one
        // synthetic event and one re-render per row before the work could start.
        // The ref guard matters because the watcher runs on nextTick, when the
        // table may already be out of the render tree (a delete run just started)
        if (this.$refs.quoteProvidersTable) {
          this.$refs.quoteProvidersTable.selectAllRows()
        }
      },
      unselectAll() {
        if (this.$refs.quoteProvidersTable) {
          this.$refs.quoteProvidersTable.clearSelected()
        }
      },
      onSearchCompanySelected(item) {
        this.filter.quoteCompanies.push({id: item.id, name: item.name})
        this.$refs.companyAc.searchQuery = ''
      },
      removeCompanyFromFilters(index) {
        this.filter.quoteCompanies.splice(index, 1)
      },
      onSearchProviderSelected(item) {
        this.filter.quoteProviders.push({id: item.id, name: item.name})
        this.$refs.providerAc.searchQuery = ''
      },
      removeProviderFromFilters(index) {
        this.filter.quoteProviders.splice(index, 1)
      },
      backupSelected() {
        const quotesExport = []
        for (let i = 0; i < this.selected.length; i++) {
          const originalQuote = JSON.parse(JSON.stringify(this.selected[i]))

          try {
            const theQuote = originalQuote
            delete (theQuote.id)
            delete (theQuote.created_at)
            delete (theQuote.updated_at)
            delete (theQuote.deleted_at)
            delete (theQuote.company)
            delete (theQuote.provider)
            delete (theQuote.area_from)
            delete (theQuote.area_to)
            delete (theQuote.shipping_type)
            delete (theQuote.size)
            quotesExport.push(theQuote)
          } catch (e) {
            console.error(e)
          }
        }

        let filename = 'quote_3p_backup'
        Object.values(this.filters).forEach(filter => {
          if (filter.length > 0) {
            // summarize long array values (e.g. the post-import ids filter) so the
            // filename never exceeds filesystem name limits
            const parts = filter.map(part => {
              return Array.isArray(part) && part.length > 10 ? part.length + '-values' : part
            })
            filename += '__' + parts.join('-')
          }
        })
        filename += '.csv'
        this.exportToCsv(filename, Papa.unparse(quotesExport))
        this.backuped = true
      },
      // one DELETE is the whole chain for a 3P quote; the pool, the retries and the
      // 404 tolerance come from bulkDeletePool.mixin
      deleteStepsFor(row) {
        return [
          {
            name: 'outsourcing-quotes',
            counter: 'outsourcingQuotes',
            run: () => { return this.deleteRequest('delete', 'outsourcing-quotes/' + row.id) }
          }
        ]
      },
      // there is no `reference` on outsourcing-quotes, so a row is identified the
      // same way the table shows it: by its client and provider. The client is
      // optional (a quote can apply to every client), hence the filter
      deleteRowIdentity(row) {
        const client = row.company && (row.company.name || row.company.id)
        const provider = row.provider && (row.provider.name || row.provider.id)
        return {id: row.id, label: [client, provider].filter(Boolean).join(' / ')}
      },
      exportToCsv(filename, csv) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        if (navigator.msSaveBlob) { // IE 10+
          navigator.msSaveBlob(blob, filename)
        } else {
          const link = document.createElement('a')
          if (link.download !== undefined) { // feature detection
            // Browsers that support HTML5 download attribute
            const url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', filename)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
        }
      }
    }
  }
</script>
